import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Tooltip } from '@gmm/ui';
import { ids as allIds } from '~/lib/constants';
import { useExerciseStatus } from '~/work/teacherWork/components/breadcrumbs/hooks';

const ids = allIds.problemPreview;

interface Props {
  disabled?: boolean;
  isLoading: boolean;
  onAdd: () => void;
  iAmLockedExam?: boolean;
  iAmRandomExam?: boolean;
  problemLoadingError?: boolean;
}

export const AddSkill: FC<Props> = ({
  disabled,
  isLoading,
  onAdd,
  iAmLockedExam,
  iAmRandomExam,
  problemLoadingError,
}) => {
  const [t] = useTranslation(['problemPreview']);
  const iAmAssignment = !(iAmLockedExam || iAmRandomExam);
  const exerciseStatus = useExerciseStatus();
  const targetIsUndefined = !exerciseStatus.isExerciseOpen;
  const targetIsLockedExam = exerciseStatus.isLockedExamOpen;
  const targetIsRandomExam = exerciseStatus.isExamOpen && !targetIsLockedExam;
  const targetIsAssignment = !(
    targetIsLockedExam ||
    targetIsRandomExam ||
    targetIsUndefined
  );

  const [addButtonDisabled, setAddButtonsDisabled] = useState(isLoading);
  const delayDisableTimer = useRef<NodeJS.Timeout>();

  let buttonText;

  if (iAmLockedExam && (targetIsLockedExam || targetIsUndefined)) {
    buttonText = t('problemPreview:addProblem');
  } else buttonText = t('problemPreview:addSkill');

  useEffect(() => {
    clearTimeout(delayDisableTimer.current!);

    if (!isLoading) return setAddButtonsDisabled(false);

    delayDisableTimer.current = setTimeout(
      () => setAddButtonsDisabled(true),
      1000,
    );

    return () => clearTimeout(delayDisableTimer.current!);
  }, [isLoading]);

  const addSkillTooltipText = useMemo((): string => {
    if (isLoading || problemLoadingError) return '';

    if (disabled) {
      if (targetIsAssignment)
        return t('problemPreview:addSkillDisabled_Assignment');

      if (iAmAssignment || iAmRandomExam) {
        return t('problemPreview:addSkillDisabled_Exam');
      } else {
        if (targetIsRandomExam)
          return t('problemPreview:addSkillDisabled_Exam');

        return t('problemPreview:addProblemDisabled_Exam');
      }
    } else if (targetIsUndefined) {
      if (iAmAssignment)
        return t('problemPreview:addSkillWillCreate_Assignment');
      if (iAmRandomExam)
        return t('problemPreview:addSkillWillCreate_RandomExam');
      if (iAmLockedExam)
        return t('problemPreview:addProblemWillCreate_LockedExam');
    }

    return '';
  }, [
    disabled,
    iAmAssignment,
    iAmLockedExam,
    iAmRandomExam,
    targetIsAssignment,
    targetIsLockedExam,
    targetIsRandomExam,
    targetIsUndefined,
    isLoading,
  ]);

  return (
    <Tooltip
      data-testid={ids.addDisabled}
      placement="top"
      title={addSkillTooltipText}
    >
      <div>
        <Button
          color="secondary"
          data-intercom-target="add-skill-to-practice-set"
          data-testid={ids.add}
          disabled={disabled || addButtonDisabled}
          onClick={onAdd}
          variant="contained"
        >
          {buttonText}
        </Button>
      </div>
    </Tooltip>
  );
};
